import { App } from 'antd'
import { useSelector } from 'react-redux'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { currentUserSelector } from '../../../redux/session/currentUser/selectors'
import { useCtxGroupCompany } from '../../companyGroup/queries/useCtxGroupCompany'
import { approveReconciliationMatches, MAX_APPROVALS_PER_REQUEST } from '../service/reconciliationService'
import { getApprovalItem, getMatchId } from '../utils/reconciliationHelpers'
import { Match, ReconciliationApproveResponse } from '../types'
import reconciliationQueryKeys from './reconciliationQueryKeys'

interface ApproveMatchesVariables {
  matches: Match[]
  comment?: string
}

// `already_approved` (e.g. a colleague got there first) counts as success.
const isSuccess = (status: string) => status === 'approved' || status === 'already_approved'

export const summarizeApproveResponse = (response: ReconciliationApproveResponse) => {
  const succeeded = response.results.filter(result => isSuccess(result.status))
  const failed = response.results.filter(result => !isSuccess(result.status))
  return { succeeded, failed }
}

export const useApproveMatches = () => {
  const { data: groupCompany } = useCtxGroupCompany()
  const groupCompanyId = groupCompany?.id
  const currentUser = useSelector(currentUserSelector)
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({ matches, comment }: ApproveMatchesVariables) => {
      if (!groupCompanyId) throw new Error('Group company id is required')
      if (matches.length === 0) throw new Error('No matches to approve')
      if (matches.length > MAX_APPROVALS_PER_REQUEST) {
        throw new Error(`Cannot approve more than ${MAX_APPROVALS_PER_REQUEST} entries at once`)
      }
      const approvals = matches.map(match => getApprovalItem(match, comment))
      return approveReconciliationMatches(groupCompanyId, approvals)
    },
    onSuccess: (response, { matches, comment }) => {
      const { succeeded, failed } = summarizeApproveResponse(response)
      // Only a fresh `approved` result with a server-issued approvalId is authoritative enough
      // to patch straight into the cache. `already_approved` (someone else got there first) or a
      // result missing its approvalId must not be stamped with the current user/comment/a
      // synthesized id — that data would be wrong. Those get a real refetch instead.
      const needsRefetch = succeeded.some(result => result.status !== 'approved' || result.approvalId === undefined)

      // Patch the accepted entries straight into the list cache instead of refetching the
      // whole (heavy) dataset. The server derives acceptedBy/snapshot etc.; the fields the
      // UI actually shows (email, timestamp, comment) we fill from the current session.
      if (groupCompanyId && succeeded.length > 0) {
        const acceptedAt = new Date().toISOString()
        const resultByIndex = new Map(response.results.map(result => [result.index, result]))
        const indexByMatchId = new Map(matches.map((match, index) => [getMatchId(match), index]))

        queryClient.setQueryData<Match[]>(reconciliationQueryKeys.list({ groupCompanyId }), current =>
          current?.map(match => {
            const index = indexByMatchId.get(getMatchId(match))
            if (index === undefined) return match
            const result = resultByIndex.get(index)
            if (!result || result.status !== 'approved' || result.approvalId === undefined) return match
            return {
              ...match,
              acceptanceStale: false,
              accepted: {
                approvalId: result.approvalId,
                acceptedBy: currentUser?.id,
                acceptedByEmail: currentUser?.email,
                acceptedAt,
                comment: comment || undefined
              }
            }
          })
        )
      }

      if (succeeded.length > 0) {
        message.success(
          t('icReconciliation:approveResult', '{{count}} eroa merkitty hyväksytyksi', { count: succeeded.length })
        )
      }
      if (failed.length > 0) {
        message.warning(
          t('icReconciliation:approveResultFailed', '{{count}} eroa ei voitu hyväksyä', { count: failed.length })
        )
      }

      // Full refetch when something was off, or an accepted entry couldn't be trusted into the
      // cache above; otherwise just mark stale for the next mount.
      queryClient.invalidateQueries({
        queryKey: reconciliationQueryKeys.lists(),
        refetchType: failed.length > 0 || needsRefetch ? 'active' : 'none'
      })
    },
    onError: () => {
      message.error(t('icReconciliation:approveError', 'Hyväksyntä epäonnistui'))
    }
  })
}
